'use client';
import Link from 'next/link';
import { Skeleton } from 'primereact/skeleton';
import { formatToIndianCurrency } from '@/_config/helpers';

const SUGGESTION_LIMIT = 7;
const PRODUCT_LIMIT = 4;

const Stars = ({ rating = 0 }) => {
    const filled = Math.round(rating);
    return (
        <span style={{ display: 'inline-flex', gap: 1 }}>
            {[1, 2, 3, 4, 5].map(s => (
                <i
                    key={s}
                    className={`pi ${s <= filled ? 'pi-star-fill' : 'pi-star'}`}
                    style={{ fontSize: '0.6rem', color: s <= filled ? '#f59e0b' : '#d1d5db' }}
                />
            ))}
        </span>
    );
};

const Badge = ({ label, color }) => (
    <span style={{
        fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px',
        borderRadius: 99, background: color, color: '#fff',
        letterSpacing: '0.03em', textTransform: 'uppercase',
    }}>
        {label}
    </span>
);

const BADGE_COLORS = { 'Best Seller': '#f59e0b', 'New': '#10b981' };

const stripHtml = (html) => html?.replace(/<[^>]*>/g, '') ?? '';

export const SearchResults = ({ data, loading, query, onSuggestionClick, onClose, inline = false }) => {
    if (!query?.trim() || query.trim().length < 2) return null;

    return (
        <div className={`search-results-panel${inline ? ' search-results-inline' : ''}`}>
            {loading && (
                <div className="search-results-loading">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="search-skeleton-row">
                            <Skeleton width="1rem" height="1rem" borderRadius="50%" />
                            <Skeleton width={`${60 + i * 15}%`} height="0.85rem" />
                        </div>
                    ))}
                </div>
            )}

            {!loading && data && (
                <>
                    {data.suggestions?.length > 0 && (
                        <div className="search-section">
                            <div className="search-section-label">Suggestions</div>
                            {data.suggestions.slice(0, SUGGESTION_LIMIT).map((s, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className="search-suggestion-row"
                                    onClick={() => onSuggestionClick(s)}
                                >
                                    <i className="pi pi-search" style={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0 }} />
                                    <span className="search-suggestion-text">{s}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {data.products?.length > 0 && (
                        <div className="search-section">
                            <div className="search-section-label">
                                Products
                                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>
                                    {data.totalResults} found
                                </span>
                            </div>
                            {data.products.slice(0, PRODUCT_LIMIT).map((p) => (
                                <Link
                                    key={p.id}
                                    href={`/${p.slug}`}
                                    className="search-product-row"
                                    onClick={onClose}
                                >
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={p.image}
                                        alt={p.title}
                                        className="search-product-img"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <div className="search-product-info">
                                        <div className="search-product-title">{p.title}</div>
                                        {p.shortDescription && (
                                            <div className="search-product-desc">{stripHtml(p.shortDescription)}</div>
                                        )}
                                        <div className="search-product-meta">
                                            <span className="search-product-price">
                                                {formatToIndianCurrency(p.price)}
                                            </span>
                                            {p.discount && (
                                                <span className="search-product-discount">{p.discount}</span>
                                            )}
                                            {p.rating > 0 && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                    <Stars rating={p.rating} />
                                                    <span style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                                                        {p.rating.toFixed(1)}
                                                    </span>
                                                </span>
                                            )}
                                            {p.badge?.map(b => (
                                                <Badge key={b} label={b} color={BADGE_COLORS[b] ?? '#6b7280'} />
                                            ))}
                                        </div>
                                    </div>
                                    <i className="pi pi-chevron-right" style={{ fontSize: '0.7rem', color: '#d1d5db', flexShrink: 0, alignSelf: 'center' }} />
                                </Link>
                            ))}
                        </div>
                    )}

                    {data.products?.length === 0 && data.suggestions?.length === 0 && (
                        <div className="search-no-results">
                            <i className="pi pi-search" style={{ fontSize: '1.5rem', color: '#e5e7eb' }} />
                            <span>No results for &ldquo;{query}&rdquo;</span>
                        </div>
                    )}

                    {data.products?.length > 0 && (
                        <Link
                            href={`/search?q=${encodeURIComponent(query)}`}
                            className="search-view-all"
                            onClick={onClose}
                        >
                            View all {data.totalResults} results for &ldquo;{query}&rdquo;
                            <i className="pi pi-arrow-right" style={{ fontSize: '0.75rem' }} />
                        </Link>
                    )}
                </>
            )}
        </div>
    );
};
